// src/app/pages/admin/AdminEventoAsociacionFormacionesWrapper.tsx
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { PageTitle } from '../../../_metronic/layout/core';
import BackButton from '../../components/BackButton/BackButton';
import styleScss from './styles.module.scss';
import {
	buscarConvocatoriasAsociacion,
	getFormacionesAsociadasEvento,
	asociarFormacionEvento,
	desasociarFormacionEvento,
} from '../../modules/apps/user-management/users-list/core/_requests';
import { LoadingBackdrop } from '../../components/LoadingSpinner/LoadingSpinner';
import {
	Alert,
	AlertTitle,
	Button,
	Paper,
	Snackbar,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
} from '@mui/material';
import { getErrorMessage } from './EventosEmpleabilidad.helpers';

dayjs.extend(utc);

const EVENTOS_LIST_PATH = '/admin/gestion-eventos';

interface FormacionAsociacion {
	id: number;
	codigo_convocatoria: string;
	fecha_registro: string;
	estado: string;
	ProgramaFormacion?: {
		id: string;
		label: string;
	};
}

interface AsociacionFormacionesPageProps {
	embedded?: boolean;
	readOnly?: boolean;
}

const AsociacionFormacionesPage = ({ embedded = false, readOnly = false }: AsociacionFormacionesPageProps = {}) => {
	const { id: eventoId } = useParams();
	const [codigoConvocatoria, setCodigoConvocatoria] = useState('');
	const [formaciones, setFormaciones] = useState<FormacionAsociacion[]>([]);
	const [asociadas, setAsociadas] = useState<number[]>([]);
	const [buscado, setBuscado] = useState(false);
	const [loading, setLoading] = useState(false);
	const [procesandoId, setProcesandoId] = useState<number | null>(null);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	const [open, setOpen] = useState(false);
	const [alert, setAlert] = useState(true);
	const [alertMsg, setAlertMsg] = useState('');

	const notify = (msg: string, success = true) => {
		setAlert(success);
		setAlertMsg(msg);
		setOpen(true);
		setTimeout(() => setOpen(false), 3000);
	};

	const mapAsociacionToFormacion = (asociacion: any): FormacionAsociacion => {
		const convocatoria = asociacion.Convocatoria || {};
		return {
			id: convocatoria.id,
			codigo_convocatoria: convocatoria.codigo_convocatoria,
			fecha_registro: convocatoria.fecha_registro,
			estado: convocatoria.estado,
			ProgramaFormacion: convocatoria.ProgramaFormacion,
		};
	};

	const cargarAsociadas = () => {
		if (!eventoId) return;
		setLoading(true);
		getFormacionesAsociadasEvento(eventoId)
			.then((response: any) => {
				const asociaciones = response?.data || [];
				setAsociadas(asociaciones.map((asociacion: any) => asociacion.convocatoria_id));
				setFormaciones(asociaciones.map(mapAsociacionToFormacion));
				setBuscado(true);
				setPage(0);
			})
			.catch((error: any) => {
				notify(getErrorMessage(error), false);
			})
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		cargarAsociadas();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eventoId]);

	const handleBuscar = () => {
		if (!codigoConvocatoria.trim()) {
			notify('Debe ingresar el código de la formación', false);
			return;
		}
		setLoading(true);
		buscarConvocatoriasAsociacion(codigoConvocatoria.trim())
			.then((response: any) => {
				setFormaciones(response?.data || []);
				setBuscado(true);
				setPage(0);
			})
			.catch((error: any) => {
				notify(getErrorMessage(error), false);
			})
			.finally(() => setLoading(false));
	};

	const handleKeyDownBuscar = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Enter') handleBuscar();
	};

	const handleLimpiarFiltros = () => {
		setCodigoConvocatoria('');
		cargarAsociadas();
	};

	const handleAsociar = (convocatoriaId: number) => {
		if (!eventoId) return;
		setProcesandoId(convocatoriaId);
		asociarFormacionEvento(eventoId, convocatoriaId)
			.then(() => {
				setAsociadas((prev) => [...prev, convocatoriaId]);
				notify('Formación asociada al evento con éxito.');
				handleLimpiarFiltros();
			})
			.catch((error: any) => {
				notify(getErrorMessage(error), false);
			})
			.finally(() => setProcesandoId(null));
	};

	const handleDesasociar = (convocatoriaId: number) => {
		if (!eventoId) return;
		setProcesandoId(convocatoriaId);
		desasociarFormacionEvento(eventoId, convocatoriaId)
			.then(() => {
				setAsociadas((prev) => prev.filter((asociadaId) => asociadaId !== convocatoriaId));
				notify('Asociación eliminada con éxito.');
				handleLimpiarFiltros();
			})
			.catch((error: any) => {
				notify(getErrorMessage(error), false);
			})
			.finally(() => setProcesandoId(null));
	};

	return (
		<div className={embedded ? '' : 'p-4 shadow-4 rounded-3 mt-15'} style={embedded ? undefined : { backgroundColor: '#FFFF' }}>
			{!embedded && <LoadingBackdrop loading={loading} />}
			{embedded ? (
				<h3 className='mb-4'>Asociación de Formaciones</h3>
			) : (
				<div className='row mb-4' style={{ alignItems: 'center' }}>
					<div className='col-sm-12 d-flex align-items-center' style={{ gap: '15px' }}>
						<BackButton to={EVENTOS_LIST_PATH} />
						<h2 className='m-0'>Asociación de Formaciones</h2>
					</div>
				</div>
			)}

			<div className='input-group d-flex flex-wrap align-items-center' style={{ gap: '10px' }}>
				<div style={{ minWidth: 280 }}>
					<TextField
						fullWidth
						size='small'
						label='Ingrese el código de la formación'
						value={codigoConvocatoria}
						onChange={(e) => setCodigoConvocatoria(e.target.value)}
						onKeyDown={handleKeyDownBuscar}
					/>
				</div>
				<Button variant='contained' onClick={handleBuscar}>
					Buscar
				</Button>
				<Button variant='outlined' disabled={!codigoConvocatoria.trim()} onClick={handleLimpiarFiltros}>
					Limpiar filtros
				</Button>
			</div>

			<div className={`${styleScss['table-limit']}`} style={{ maxWidth: '100%', overflowWrap: 'break-word', overflowY: 'hidden' }}>
				<Stack padding={2}>
					<TableContainer component={Paper} style={{ overflowY: 'visible', overflowWrap: 'break-word' }}>
						<Table style={{ overflowY: 'visible' }}>
							<TableHead className={`${styleScss['th-custom']}`}>
								<TableRow>
									<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Código de convocatoria</h4></TableCell>
									<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Programa de formación</h4></TableCell>
									<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Fecha de registro</h4></TableCell>
									<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Estado</h4></TableCell>
									<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Acciones</h4></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{formaciones.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((formacion) => {
									const estaAsociada = asociadas.includes(formacion.id);
									const procesando = procesandoId === formacion.id;
									return (
										<TableRow key={formacion.id}>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{formacion.codigo_convocatoria}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{formacion.ProgramaFormacion?.label}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{formacion.fecha_registro ? dayjs(formacion.fecha_registro).utc().format('DD/MM/YYYY') : ''}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{formacion.estado}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
												<Stack direction='column' spacing={1} alignItems='center'>
													<Button
														variant='contained'
														size='small'
														disabled={estaAsociada || procesando || readOnly}
														onClick={() => handleAsociar(formacion.id)}
													>
														Agregar asociación
													</Button>
													<Button
														variant='outlined'
														color='error'
														size='small'
														disabled={!estaAsociada || procesando || readOnly}
														onClick={() => handleDesasociar(formacion.id)}
													>
														Eliminar asociación
													</Button>
												</Stack>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
							{!loading && buscado && formaciones.length === 0 && (
								<tfoot>
									<TableRow>
										<TableCell colSpan={5}>
											<p className='text-center' style={{ width: '100%', textAlign: 'center' }}>
												No se encontraron registros
											</p>
										</TableCell>
									</TableRow>
								</tfoot>
							)}
						</Table>
						<TablePagination
							rowsPerPageOptions={[10, 20, 50]}
							component='div'
							style={{ overflow: 'visible', fontFamily: 'Poppins', fontSize: '14.5px' }}
							count={formaciones.length}
							rowsPerPage={rowsPerPage}
							page={page}
							labelRowsPerPage='Filas por página'
							onPageChange={(_event, newPage) => setPage(newPage)}
							onRowsPerPageChange={(event) => {
								setRowsPerPage(parseInt(event.target.value, 10));
								setPage(0);
							}}
						/>
					</TableContainer>
				</Stack>
			</div>

			<Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'right' }} open={open} onClose={() => setOpen(false)}>
				<Alert onClose={() => setOpen(false)} variant='filled' severity={alert ? 'success' : 'error'} sx={{ whiteSpace: 'pre-line' }}>
					{alert ? <AlertTitle>Éxito</AlertTitle> : <AlertTitle>Error</AlertTitle>}
					{alertMsg}
				</Alert>
			</Snackbar>
		</div>
	);
};

const AdminEventoAsociacionFormacionesWrapper = () => {
	return (
		<>
			<PageTitle breadcrumbs={[]}>Asociación de Formaciones</PageTitle>
			<AsociacionFormacionesPage />
		</>
	);
};

export { AdminEventoAsociacionFormacionesWrapper, AsociacionFormacionesPage };
